"use client";

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTokenData } from "../../../../global-account/react/index.js";
import { formatTokenAmount, safeBigInt } from "../../../../shared/utils/number.js";
import { cn } from "../../../../shared/utils/cn.js";
import { useMemo } from "react";
import { AnySpendFingerprintWrapper, getFingerprintConfig } from "../AnySpendFingerprintWrapper.js";
import { AnySpendCustomizationProvider } from "../context/AnySpendCustomizationContext.js";
import { CheckoutCartPanel } from "./CheckoutCartPanel.js";
import { CheckoutPaymentPanel } from "./CheckoutPaymentPanel.js";
import { PoweredByBranding } from "./PoweredByBranding.js";
export function AnySpendCheckoutTrigger({
  recipientAddress,
  destinationTokenAddress,
  destinationTokenChainId,
  items,
  totalAmount: totalAmountOverride,
  organizationName,
  organizationLogo,
  themeColor,
  buttonText = "Pay",
  workflowId,
  orgId,
  callbackMetadata,
  onSuccess,
  onError,
  returnUrl,
  returnLabel,
  classes,
  footer,
  defaultPaymentMethod,
  senderAddress,
  slots,
  content,
  theme,
  shipping,
  tax,
  discount,
  summaryLines,
  kycEnabled = false,
  showFiatOption
}) {
  // Merge workflowId + orgId into callbackMetadata
  const mergedMetadata = useMemo(() => {
    if (!workflowId && !orgId && !callbackMetadata) return undefined;
    return {
      ...(workflowId ? {
        workflowId
      } : {}),
      ...(orgId ? {
        orgId
      } : {}),
      ...callbackMetadata
    };
  }, [workflowId, orgId, callbackMetadata]);
  // Compute total from items + adjustments or use override
  const computedTotal = useMemo(() => {
    if (totalAmountOverride) return totalAmountOverride;
    if (!items || items.length === 0) return "0";
    let total = BigInt(0);
    for (const item of items) {
      total += safeBigInt(item.amount) * BigInt(item.quantity);
    }
    const shippingAmt = typeof shipping === "string" ? shipping : shipping?.amount;
    if (shippingAmt) total += safeBigInt(shippingAmt);
    const taxAmt = typeof tax === "string" ? tax : tax?.amount;
    if (taxAmt) total += safeBigInt(taxAmt);
    const discountAmt = typeof discount === "string" ? discount : discount?.amount;
    if (discountAmt) total -= safeBigInt(discountAmt);
    if (summaryLines) {
      for (const line of summaryLines) total += safeBigInt(line.amount);
    }
    if (total < BigInt(0)) total = BigInt(0);
    return total.toString();
  }, [items, totalAmountOverride, shipping, tax, discount, summaryLines]);
  // Get destination token metadata
  const {
    data: tokenData
  } = useTokenData(destinationTokenChainId, destinationTokenAddress);
  const tokenSymbol = tokenData?.symbol || "";
  const tokenDecimals = tokenData?.decimals || 18;
  const formattedTotal = useMemo(() => formatTokenAmount(BigInt(computedTotal || "0"), tokenDecimals), [computedTotal, tokenDecimals]);
  const hasItems = items && items.length > 0;
  const fingerprint = getFingerprintConfig();
  return _jsx(AnySpendFingerprintWrapper, {
    fingerprint: fingerprint,
    children: _jsx(AnySpendCustomizationProvider, {
      slots: slots,
      content: content,
      theme: theme,
      children: _jsxs("div", {
        className: "anyspend-checkout-trigger flex flex-col",
        children: [hasItems && _jsx("div", {
          className: "border-b border-gray-200 p-5 dark:border-neutral-700",
          children: _jsx(CheckoutCartPanel, {
            items: items,
            totalAmount: computedTotal,
            tokenSymbol: tokenSymbol,
            tokenDecimals: tokenDecimals,
            organizationName: organizationName,
            organizationLogo: organizationLogo,
            classes: classes,
            footer: footer,
            shipping: typeof shipping === "string" ? {
              amount: shipping
            } : shipping,
            tax: typeof tax === "string" ? {
              amount: tax
            } : tax,
            discount: typeof discount === "string" ? {
              amount: discount
            } : discount,
            summaryLines: summaryLines
          })
        }), !hasItems && _jsx("div", {
          className: "border-b border-gray-200 p-5 dark:border-neutral-700",
          children: _jsxs("div", {
            className: "flex flex-col gap-3",
            children: [_jsxs("div", {
              className: cn("flex items-center justify-between", classes?.cartSummary),
              children: [_jsx("span", {
                className: "text-base font-semibold text-gray-900 dark:text-gray-100",
                children: "Total"
              }), _jsxs("span", {
                className: cn("text-base font-semibold text-gray-900 dark:text-gray-100", classes?.cartTotal),
                children: [formattedTotal, " ", tokenSymbol]
              })]
            }), footer === undefined ? _jsx(PoweredByBranding, {
              organizationName: organizationName,
              organizationLogo: organizationLogo,
              classes: classes
            }) : footer]
          })
        }), _jsx("div", {
          className: "px-2 py-3",
          children: _jsx(CheckoutPaymentPanel, {
            recipientAddress: recipientAddress,
            destinationTokenAddress: destinationTokenAddress,
            destinationTokenChainId: destinationTokenChainId,
            totalAmount: computedTotal,
            buttonText: buttonText,
            themeColor: themeColor,
            returnUrl: returnUrl,
            returnLabel: returnLabel,
            onSuccess: onSuccess,
            onError: onError,
            callbackMetadata: mergedMetadata,
            classes: classes,
            defaultPaymentMethod: defaultPaymentMethod,
            senderAddress: senderAddress,
            kycEnabled: kycEnabled,
            showFiatOption: showFiatOption
          })
        })]
      })
    })
  });
}